// src/pages/DashboardPage.jsx
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  UserGroupIcon,
  BookOpenIcon,
  CurrencyDollarIcon,
  ChartBarIcon,
  CalendarDaysIcon,
  UserPlusIcon,
  PlusIcon,
  CreditCardIcon,
  ArrowLeftIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from "@heroicons/react/24/outline";
import { motion } from "framer-motion";
import { getStudentsCount, getClassCount, getStudentsWithIncompleteFees,getStaticSummary } from "../services/api";
import { UserRole } from '../types';
import { useAuth } from '../hooks/useAuth';
import { toast } from "react-toastify";

const DashboardPage = () => {
  const navigate = useNavigate();
  const auth = useAuth();
  const role = auth?.user?.role || auth?.role || (Array.isArray(auth?.user?.roles) ? auth.user.roles[0] : null);

  const isAccountant = role === UserRole.ACCOUNTANT;
  const isSecretary = role === UserRole.SECRETARY;

  // ------------------- STATE -------------------
  const [studentsCount, setStudentsCount] = useState(0);
  const [classCount, setClassCount] = useState(0);
  const [incompleteFeesCount, setIncompleteFeesCount] = useState(0);
  const [staticSummaryCount, setstaticSummaryCount] = useState(0);

  const [loading, setLoading] = useState(true);

  // --- New Calendar State ---
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [displayMonth, setDisplayMonth] = useState(new Date());

  // ------------------- FETCH STATS -------------------
  useEffect(() => {
    const fetchStats = async () => {
      try {
        setLoading(true);
        const [students, classes, incompleteFees,staticSummary] = await Promise.all([
          getStudentsCount(),
          getClassCount(),
          getStudentsWithIncompleteFees(),
          getStaticSummary()
        ]);

        setStudentsCount(students.count);
        setClassCount(classes.count);
        setIncompleteFeesCount(incompleteFees.incompleteCount);
        setstaticSummaryCount(staticSummary.count || 0);

      } catch (error) {
        console.error("Error fetching dashboard stats:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, []);

  // ------------------- DATA FOR CARDS -------------------
  // Each card onClick checks role and either navigates or shows a localized message.
  const stats = [
    {
      id: 'students-card',
      title: "إجمالي الطلاب :",
      value: loading ? "..." : studentsCount,
      icon: UserGroupIcon,
      gradient: "from-blue-500 to-indigo-600",
      onClick: () => navigate("/students"),
    },
    {
      id: 'classes-card',
      title: "إجمالي الشعب لهذا العام  :",
      value: loading ? "..." : classCount,
      icon: BookOpenIcon,
      gradient: "from-green-500 to-emerald-600",
      onClick: () => {
        if (isAccountant) {
          // Accountant not allowed to open classes page
          toast.info("عذراً — حساب المحاسب لا يملك صلاحية فتح صفحة الشعب.", { autoClose: 3000 });
          return;
        }
        navigate("/classes");
      },
    },
    {
      id: 'incomplete-fees-card',
      title: "  الأقساط المتبقية لهذا العام  : ",
      value: loading ? "..." : incompleteFeesCount,
      icon: CurrencyDollarIcon,
      gradient: "from-yellow-500 to-amber-600",
      onClick: () => {
        if (isSecretary) {
          toast.info("عذراً — حساب أمانة السر لا يملك صلاحية فتح هذه الصفحة   .", { autoClose: 3000 });
          // Secretary not allowed to open incomplete-fees
          return;
        }
        navigate("/incomplete-fees");
      },
    },    {
      id: 'staticFees',
      title: "ذمم الطلاب المنسحبين : ",
      value: loading ? "..." : staticSummaryCount,
      icon: CurrencyDollarIcon,
      gradient: "from-blue-700 to-indigo-400",
      onClick: () => {
        if (isSecretary) {
          toast.info("عذراً — حساب أمانة السر لا يملك صلاحية فتح هذه الصفحة   .", { autoClose: 3000 });
          // Secretary not allowed to open incomplete-fees
          return;
        }
        navigate("/staticFees");
      },
    },
  
  ];

  // ------------------- QUICK ACTIONS (role-based) -------------------
  // We build a list based on the role
  const buildQuickActions = () => {
    // helper to create action object
    const make = (title, icon, onClick) => ({ title, icon, onClick });

    if (isAccountant) {
      return [
        make("تسجيل دفعة", CreditCardIcon, () => navigate('/accounting', { state: { autoOpenTab: 'payment' } })),
        make("تسجيل نفقة", CurrencyDollarIcon, () => navigate('/accounting', { state: { autoOpenTab: 'expense' } })),
      ];
    }

    if (isSecretary) {
      return [
        make("إضافة طالب جديد", UserPlusIcon, () => navigate('/students', { state: { autoOpenModal: true } })),
        make("إضافة شعبة جديدة", PlusIcon, () => navigate('/classes', { state: { autoOpenModal: true } })),
      ];
    }

    // Owner (and fallback): show all actions (original ones + expense)
    return [
      make("إضافة طالب جديد", UserPlusIcon, () => navigate('/students', { state: { autoOpenModal: true } })),
      make("إضافة شعبة جديدة", PlusIcon, () => navigate('/classes', { state: { autoOpenModal: true } })),
      make("تسجيل دفعة", CreditCardIcon, () => navigate('/accounting', { state: { autoOpenTab: 'payment' } })),
      make("تسجيل نفقة", CurrencyDollarIcon, () => navigate('/accounting', { state: { autoOpenTab: 'expense' } })),
    ];
  };

  const quickActions = buildQuickActions();

  // ------------------- CALENDAR LOGIC -------------------
  const generateCalendar = () => {
    const today = new Date();
    const year = displayMonth.getFullYear();
    const month = displayMonth.getMonth();

    const monthName = displayMonth.toLocaleDateString('ar-SY', { month: 'long', year: 'numeric' });
    const firstDayOfMonth = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();

    const paddingDays = Array(firstDayOfMonth).fill(null);
    const days = Array.from({ length: daysInMonth }, (_, i) => i + 1);
    const calendarDays = [...paddingDays, ...days];

    const isSameDay = (date1, date2) =>
      date1.getDate() === date2.getDate() &&
      date1.getMonth() === date2.getMonth() &&
      date1.getFullYear() === date2.getFullYear();

    return { monthName, calendarDays, isSameDay, today };
  };

  const { monthName, calendarDays, isSameDay, today } = generateCalendar();

  const changeMonth = (offset) => {
    setDisplayMonth(prev => new Date(prev.getFullYear(), prev.getMonth() + offset, 1));
  };

  // ------------------- ANIMATION VARIANTS -------------------
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1, delayChildren: 0.2 },
    },
  };
  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { type: "spring", stiffness: 100 },
    },
  };

  // ------------------- RENDER -------------------
  return (
    <div dir="rtl" className="p-6 sm:p-8 space-y-9 bg-gradient-to-br from-gray-50 to-blue-100 min-h-screen font-sans">
      {/* Header */}
      <motion.header
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="space-y-1"
      >
        <h1 className="text-4xl font-extrabold text-gray-800 tracking-tight">لوحة التحكم</h1>
        <p className="text-gray-700 text-lg">نظرة عامة على أداء مدرستك.</p>
      </motion.header>

      {/* Top Stat Cards */}
      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-6"
      >
        {stats.map((stat, i) => (
          <motion.div
            key={i}
            variants={itemVariants}
            onClick={stat.onClick}
            className={`relative p-6 rounded-2xl shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300 cursor-pointer overflow-hidden text-white bg-gradient-to-br ${stat.gradient}`}
          >
            <div className="relative z-10">
              <h3 className="text-lg font-medium opacity-80">{stat.title}</h3>
              <p className="text-4xl font-bold mt-2">{stat.value}</p>
            </div>
            <stat.icon className="absolute -bottom-4 -left-4 h-24 w-24 text-white opacity-10 z-0" />
          </motion.div>
        ))}
      </motion.div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Calendar */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0, transition: { delay: 0.5 } }}
          className="lg:col-span-2 bg-white/80 backdrop-blur-sm shadow-lg rounded-2xl p-6 border border-gray-100"
        >
          <div className="flex justify-between items-center mb-5">
            <h2 className="text-lg font-semibold text-gray-700 flex items-center gap-2">
              <CalendarDaysIcon className="h-6 w-6 text-indigo-500" />
              التقويم اليومي
            </h2>
            <div className="flex items-center gap-2">
              <button
                onClick={() => changeMonth(-1)}
                className="p-2 rounded-full hover:bg-indigo-100 text-indigo-600 transition-colors"
                aria-label="الشهر السابق"
              >
                <ChevronRightIcon className="h-5 w-5" />
              </button>
              <span className="text-sm font-medium bg-indigo-100 text-indigo-700 py-1 px-3 rounded-full w-32 text-center">
                {monthName}
              </span>
              <button
                onClick={() => changeMonth(1)}
                className="p-2 rounded-full hover:bg-indigo-100 text-indigo-600 transition-colors"
                aria-label="الشهر التالي"
              >
                <ChevronLeftIcon className="h-5 w-5" />
              </button>
            </div>
          </div>

          <div className="grid grid-cols-7 gap-2 text-center">
            {['الأحد', 'الاثنين', 'الثلاثاء', 'الأربعاء', 'الخميس', 'الجمعة', 'السبت'].map(day => (
              <div key={day} className="text-xs font-semibold text-gray-500 mb-2">
                {day}
              </div>
            ))}

            {calendarDays.map((day, index) => {
              if (!day) return <div key={`pad-${index}`} className="h-12 w-12"></div>;

              const date = new Date(displayMonth.getFullYear(), displayMonth.getMonth(), day);
              const isTodayDay = isSameDay(date, today);
              const isSelectedDay = isSameDay(date, selectedDate);

              return (
                <button
                  key={day}
                  onClick={() => setSelectedDate(date)}
                  className={`h-12 w-12 rounded-full text-sm font-medium transition-all duration-200 flex items-center justify-center relative
                    ${
                      isSelectedDay
                        ? "bg-indigo-600 text-white shadow-md scale-110"
                        : isTodayDay
                        ? "bg-indigo-100 text-indigo-700"
                        : "hover:bg-gray-100 text-gray-700"
                    }
                  `}
                >
                  {day}
                  {isTodayDay && !isSelectedDay && (
                    <span className="absolute bottom-1.5 h-1 w-1 bg-indigo-600 rounded-full"></span>
                  )}
                </button>
              );
            })}
          </div>
        </motion.div>

        {/* Quick Actions Panel */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0, transition: { delay: 0.6 } }}
          className="bg-white/80 backdrop-blur-sm shadow-lg rounded-2xl p-6 border border-gray-100 flex flex-col"
        >
          <h2 className="text-lg font-semibold text-slate-900 mb-4">إجراءات سريعة</h2>
          <div className="space-y-3 flex-grow flex flex-col">
            {quickActions.map((action, i) => (
              <button
                key={i}
                onClick={action.onClick}
                className="group flex items-center justify-between w-full p-4 rounded-xl bg-slate-50 hover:bg-indigo-500 transition-all duration-300"
              >
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-slate-200 group-hover:bg-white/20 rounded-lg transition-colors">
                    <action.icon className="h-5 w-5 text-slate-700 group-hover:text-white transition-colors" />
                  </div>
                  <span className="font-semibold text-slate-900 group-hover:text-white transition-colors">
                    {action.title}
                  </span>
                </div>
                <ArrowLeftIcon className="h-5 w-5 text-slate-600 group-hover:text-white opacity-0 group-hover:opacity-100 -translate-x-2 group-hover:translate-x-0 transition-all duration-300"/>
              </button>
            ))}
          </div>
        </motion.div>
      </div>

    </div>
  );
};

export default DashboardPage;
